import AppShell from '@/components/feature/AppShell';
import { useTranslation } from 'react-i18next';
import MetricCard from '@/pages/dashboard/admin/components/MetricCard';
import ProgressChart from '@/pages/dashboard/therapist/components/ProgressChart';
import UpcomingSessions from '@/pages/dashboard/therapist/components/UpcomingSessions';
import SessionNotesList from '@/pages/dashboard/therapist/components/SessionNotesList';
import CaseloadGrid from '@/pages/dashboard/therapist/components/CaseloadGrid';
import CheckInWidget from '@/components/feature/CheckInWidget';
import { useCheckIn } from '@/hooks/useCheckIn';
import { therapistMetricCards, therapistNotifications } from '@/mocks/dashboard';
import { useCurrentUser } from '@/hooks/useCurrentUser';

const therapistMetricLabelKeys: Record<string, string> = {
  caseload: 'totalStudents',
  sessions: 'totalSessions',
  pending: 'pendingNotes',
  progress: 'avgProgress',
};

export default function TherapistDashboard() {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { isCheckedOut } = useCheckIn(currentUser.name);
  const now = new Date();
  const hour = now.getHours();
  const greeting =
    hour < 12 ? t('common.goodMorning') : hour < 17 ? t('common.goodAfternoon') : t('common.goodEvening');

  return (
    <AppShell
      pageTitle={t('dashboard.therapistDashboard')}
      pageSubtitle={t('dashboard.therapistSubtitle')}
      breadcrumbs={[{ label: t('common.home') }, { label: t('common.dashboard') }]}
      notifications={therapistNotifications}
    >
      {/* Greeting Banner */}
      <div className="gradient-hero rounded-2xl p-4 sm:p-6 mb-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, oklch(0.72 0.17 58), transparent 70%)', transform: 'translate(30%, -30%)' }}
        />
        <div className="absolute bottom-0 left-1/3 w-32 h-32 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, oklch(0.60 0.12 215), transparent 70%)', transform: 'translateY(40%)' }}
        />

        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="text-foreground-300 text-xs sm:text-sm font-label font-medium mb-1">
              {now.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            <h2 className="font-heading font-700 text-foreground-50 text-xl sm:text-2xl">
              {greeting}, {currentUser.name.split(' ')[0]}! 👋
            </h2>
            <p className="text-foreground-300 text-xs sm:text-sm font-label mt-1">
              You have <span className="text-primary-400 font-medium">3 sessions today</span> and <span className="text-accent-400 font-medium">6 notes pending</span>.
            </p>
            {isCheckedOut && (
              <p className="text-semantic-success text-xs font-label font-medium mt-1.5 flex items-center gap-1">
                <i className="ri-check-double-line" />
                {t('dashboard.attendanceRecorded')}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <button className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex-1 sm:flex-none">
              <i className="ri-add-line" />
              {t('dashboard.newSessionNote')}
            </button>
            <button className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-foreground-50/10 border border-foreground-50/20 text-foreground-100 text-sm font-label font-medium rounded-lg hover:bg-foreground-50/20 transition-colors cursor-pointer whitespace-nowrap flex-1 sm:flex-none">
              <i className="ri-file-chart-2-line" />
              {t('dashboard.startAssessment')}
            </button>
          </div>
        </div>
      </div>

      {/* Check-In / Check-Out Widget */}
      <CheckInWidget staffName={currentUser.name} />

      {/* Dashboard content */}
      <div>
        {/* Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          {therapistMetricCards.map((metric) => (
            <MetricCard
              key={metric.id}
              label={t(`dashboard.${therapistMetricLabelKeys[metric.id] || metric.label}`, { defaultValue: metric.label })}
              value={metric.value}
              change={metric.change}
              trend={metric.trend as 'up' | 'down' | 'warning' | 'neutral'}
              icon={metric.icon}
              iconBg={metric.iconBg}
              iconColor={metric.iconColor}
              accent={metric.accent}
            />
          ))}
        </div>

        {/* Main content: Progress Chart + Schedule */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6 mb-6">
          <ProgressChart />
          <UpcomingSessions />
        </div>

        {/* Session Notes */}
        <div className="mb-6">
          <SessionNotesList />
        </div>

        {/* Caseload Grid */}
        <CaseloadGrid />
      </div>
    </AppShell>
  );
}